import React, { useState, useRef } from 'react';
import { Upload, Loader2, X, Image as ImageIcon } from 'lucide-react';
import { adminUploadImage } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

/**
 * Image upload control:
 * - Shows preview of current value (data URL or external URL)
 * - Upload button uses /api/admin/upload to convert to base64 data URL
 * - Also allows pasting external URL manually
 */
const ImageUpload = ({ value, onChange, label = 'Imagen', accept = 'image/*', aspect = 'video' }) => {
  const [uploading, setUploading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const inputRef = useRef(null);
  const { toast } = useToast();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Archivo demasiado grande', description: 'Máximo 5MB.' });
      return;
    }
    setUploading(true);
    try {
      const { url } = await adminUploadImage(file);
      onChange(url);
      toast({ title: 'Imagen subida correctamente' });
    } catch (err) {
      toast({ title: 'Error al subir', description: err.response?.data?.detail || 'Inténtalo de nuevo' });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const aspectClass = aspect === 'poster' ? 'aspect-[2/3]' : aspect === 'square' ? 'aspect-square' : 'aspect-video';

  return (
    <div>
      {label && (
        <span className="block text-[11px] uppercase tracking-widest text-white/50 mb-2">{label}</span>
      )}

      <div className={`relative w-full ${aspectClass} max-w-xs rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden mb-3`}>
        {value ? (
          <>
            <img src={value} alt="preview" className="w-full h-full object-contain" />
            <button
              type="button"
              onClick={() => onChange('')}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70 backdrop-blur text-white/80 hover:text-white hover:bg-red-500/80 transition-colors"
              title="Eliminar"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/30">
            <ImageIcon className="w-8 h-8 mb-2" />
            <span className="text-[11px] uppercase tracking-widest">Sin imagen</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-[#00e0c7]/10 border border-[#00e0c7]/30 text-[#9be8dc] hover:bg-[#00e0c7]/15 text-[13px] font-medium transition-colors disabled:opacity-50"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? 'Subiendo...' : 'Subir archivo'}
        </button>
        <button
          type="button"
          onClick={() => setShowUrl((v) => !v)}
          className="text-[12px] text-white/50 hover:text-white px-2 transition-colors"
        >
          {showUrl ? 'Ocultar URL' : 'Pegar URL externa'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleFile}
          className="hidden"
        />
      </div>

      {showUrl && (
        <input
          className="ldc-input mt-3"
          placeholder="https://..."
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
};

export default ImageUpload;
